import clsx from 'clsx';
import { t } from 'i18next';

import { OpSelect, OpSelectSingleProps } from '../OpSelect/OpSelect';

import timezones from './timezones.json';

/**
 * OpTimezonePicker is a specialized select component for choosing timezones.
 * It provides a searchable dropdown of all available timezones with their labels and values.
 *
 * Use this component whenever users need to select a timezone for scheduling, events,
 * user preferences, or any time-sensitive features. Includes all standard timezones
 * with proper IANA timezone identifiers.
 *
 * @example
 * <OpTimezonePicker
 *   value={selectedTimezone}
 *   onChange={handleTimezoneChange}
 * />
 *
 */
export const OpTimezonePicker = ({
  testId = 'op-timezone-picker',
  gtm,
  children,
  className,
  includeDeviceTimezone = false,
  ...opSelectProps
}: Omit<OpSelectSingleProps<string, any>, 'options'> & {
  includeDeviceTimezone?: boolean;
}) => {
  const timezoneOptions = Object.entries(timezones).map(([label, value]) => ({
    value,
    label,
  }));

  if (includeDeviceTimezone) {
    timezoneOptions.unshift({
      value: 'Device',
      label: t('Device local time'),
    });
  }

  return (
    <OpSelect
      className={clsx('op-timezone-picker', className)}
      testId={testId}
      gtm={gtm}
      showSearch
      {...opSelectProps}
      options={timezoneOptions}
    />
  );
};
